/**
 * Cobrarle a un cliente.
 *
 * ES EL FLUJO MAS FRECUENTE DESPUES DE VENDER, Y EL QUE MAS CARO SALE ERRAR
 * ------------------------------------------------------------------------
 * Tres cosas que este dialogo tiene que dejar clarisimas:
 *
 * 1. CUANTO DEBE, EN LA MONEDA QUE CORRESPONDE. Un cliente con cuenta anclada
 *    al dolar tiene DOS numeros legitimos: su deuda en dolares y lo que hay que
 *    cobrarle hoy en pesos. Mostrar uno solo hace que alguien cobre de menos.
 *
 * 2. LA MONEDA LA IMPONE LA CAJA. Se elige "Efectivo (pesos)" y el importe
 *    queda en pesos. La mayoria paga en pesos aunque la deuda este en dolares, y
 *    eso funciona: la caja recibe pesos y la deuda baja por el equivalente.
 *
 * 3. CUANTO QUEDA DESPUES. Antes de confirmar se ve el saldo resultante. Es la
 *    unica forma de que alguien note que puso un cero de mas.
 *
 * Sobre `ui/dialog` (Radix): Escape cierra, el foco queda atrapado adentro y
 * vuelve al boton que lo abrio. El resumen de "que queda despues" va entre el
 * cuerpo y el footer, fuera del scroll, para que siga a la vista con diez
 * medios de pago cargados.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check } from "lucide-react";

import { convertir, formatearCotizacion, redondear } from "@tora/shared";

import { aNumero } from "@/lib/tipos";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Kpi } from "@/components/ui/kpi";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Importe } from "@/components/money/importe";
import { BadgeMora } from "@/components/cobro/BadgeMora";
import { EditorPagos, nuevoPago } from "@/components/cobro/EditorPagos";
import {
  type CuentaFinanciera,
  type DatosCheque,
  type DeudorFila,
  type PagoCobro,
  chequeCompleto,
  esCheque,
  evaluarCobro,
  cajaDelVuelto,
  totalCobrado,
} from "@/components/cobro/logica";

export interface DialogCobroProps {
  cliente: DeudorFila | null;
  cuentas: CuentaFinanciera[];
  cotizacion: number;
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (
    pagos: Array<{
      cuenta_id: number;
      medio: string;
      importe: number;
      cheque?: DatosCheque & { tipo: "CHEQUE" | "ECHEQ" };
    }>,
    clave: string,
    /**
     * (0155) Lo pagado de mas es vuelto en pesos, como en la venta; con
     * `vuelto_propina` el cliente lo deja.
     */
    opciones?: { vuelto_propina: boolean },
  ) => void;
}

export function DialogCobro({
  cliente,
  cuentas,
  cotizacion,
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogCobroProps): JSX.Element | null {
  const [pagos, setPagos] = useState<PagoCobro[]>([]);
  const [propina, setPropina] = useState(false);
  /**
   * La llave de idempotencia de ESTE cobro.
   *
   * Nace al abrir el diálogo para este cliente y no cambia mientras siga
   * abierto: mandarlo dos veces —el corte de red y el "Registrar el cobro"
   * apretado de nuevo— es el mismo intento, y el servidor tiene que cobrar una
   * sola vez. Ver 0071.
   */
  const clave = useRef(crypto.randomUUID());

  const saldo = cliente ? aNumero(cliente.saldo) : 0;
  const anclaje = cliente?.moneda_anclaje ?? "ARS";
  const enDolares = anclaje === "USD";

  // Al abrir, se propone un pago por el total de la deuda: es lo que pasa la
  // mayoria de las veces, y el que paga parcial solo tiene que corregir el
  // numero en vez de calcularlo.
  useEffect(() => {
    // Cliente nuevo, cobro nuevo, llave nueva.
    clave.current = crypto.randomUUID();
    setPropina(false);
    if (!cliente) {
      setPagos([]);
      return;
    }
    const primero = nuevoPago(cuentas, [], saldo, anclaje, cotizacion);
    setPagos(primero ? [primero] : []);
  }, [cliente, cuentas, saldo, anclaje, cotizacion]);

  const resultado = useMemo(
    () => evaluarCobro(saldo, pagos, cuentas, anclaje, cotizacion),
    [saldo, pagos, cuentas, anclaje, cotizacion],
  );

  if (!cliente) return null;

  const saldoArsHoy = aNumero(cliente.saldo_ars_hoy);
  const impagos = aNumero(cliente.comprobantes_impagos);

  // Un pago con `importe: null` es un campo que no se entiende (o quedó vacío).
  // No se descarta en silencio: bloquea el cobro hasta que se corrija o se quite.
  const validos = pagos.filter(
    (p): p is PagoCobro & { importe: number } => p.importe !== null && p.importe > 0,
  );
  const hayInvalidos = pagos.some((p) => p.importe === null);
  // Un cheque sin sus datos no se manda: el servidor lo rechaza entero
  // (clientes.ts:299) y el usuario se come un error que no puede arreglar desde
  // ninguna pantalla. Se frena antes, con el aviso al lado.
  const chequeIncompleto = pagos.some((p) => !chequeCompleto(p));
  /*
    (0155) LO PAGADO DE MAS ES VUELTO. Antes quedaba a favor del cliente y el
    cajero daba el vuelto igual: el cajon quedaba corto. Es la regla de la
    venta (0145): vuelto en pesos del cajon de Efectivo, o propina si lo deja.
    La misma cuenta que hace la base: los pesos que entraron menos la deuda
    en pesos, a la cotizacion de la pantalla.
  */
  const hayVuelto = resultado.quedaAFavor;
  const vueltoArs = hayVuelto
    ? redondear(
        totalCobrado(pagos, cuentas, "ARS", cotizacion) -
          convertir(Math.max(saldo, 0), anclaje, "ARS", cotizacion),
      )
    : 0;
  const cajonVuelto = hayVuelto ? cajaDelVuelto(pagos, cuentas) : null;
  const vueltoSinCajon = hayVuelto && !propina && cajonVuelto === null;
  const puede =
    validos.length > 0 && !hayInvalidos && !chequeIncompleto && !vueltoSinCajon && !guardando;

  // Saldado por los centavos de la conversion (0121_8): no queda nada, aunque
  // la cuenta de los pagos no de cero justo.
  const restaArs = resultado.cancelaTodo
    ? 0
    : convertir(Math.abs(resultado.resta), anclaje, "ARS", cotizacion);

  /*
    LA EQUIVALENCIA, SIEMPRE QUE HAYA DOS MONEDAS.
    Antes se mostraba solo en las cuentas en dolares. Cobrarle con dolares a una
    cuenta en pesos tambien convierte, y sin verlo no se entendia por que
    US$ 33,80 dejaba «Le sigue quedando $ 8» y US$ 33,81 «a favor $ 7,90».
  */
  const otraMoneda = validos.some(
    (p) => (cuentas.find((c) => c.id === p.cuenta_id)?.moneda ?? anclaje) !== anclaje,
  );
  /*
    EL GRANO. Un centavo de la otra moneda, en la moneda de la cuenta: con el
    dolar a 1.590, US$ 0,01 son $ 15,90, y una deuda en pesos no se puede
    cancelar exacto con dolares. No se redondea solo —seria perdonar o cobrar
    plata sin que nadie lo decida—: se avisa, y quien cobra elige.
  */
  const grano = otraMoneda
    ? convertir(0.01, anclaje === "ARS" ? "USD" : "ARS", anclaje, cotizacion)
    : 0;
  const quedaElGrano = !resultado.cancelaTodo && grano > 0.01 && Math.abs(resultado.resta) < grano;

  return (
    <Dialog
      open
      onOpenChange={(abierto) => {
        if (!abierto && !guardando) onCerrar();
      }}
    >
      <DialogContent tamano="md" mobile="hoja">
        <DialogHeader>
          <DialogTitle>Cobrarle a {cliente.nombre}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <BadgeMora estado={cliente.estado_mora} />
            <span>
              {impagos === 1 ? "1 comprobante impago" : `${impagos} comprobantes impagos`}
              {/* Con una venta de hoy decia «de hace 0 días». */}
              {cliente.antiguedad_deuda !== null &&
                (cliente.antiguedad_deuda <= 0
                  ? " · el más viejo, de hoy"
                  : ` · el más viejo, de hace ${cliente.antiguedad_deuda} ${cliente.antiguedad_deuda === 1 ? "día" : "días"}`)}
            </span>
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {/* La deuda. Con cuenta anclada al dolar se muestran los DOS numeros y
              se dice cual se le cobra hoy: son los dos legitimos, y mostrar uno
              solo hace que alguien cobre de menos. */}
          <Kpi
            etiqueta="Debe"
            monto={saldoArsHoy}
            moneda="ARS"
            nota={
              enDolares ? (
                <>
                  Su cuenta está anclada al dólar: la deuda son{" "}
                  <Importe monto={saldo} moneda="USD" tamano="sm" />, y hoy eso son{" "}
                  <Importe monto={saldoArsHoy} moneda="ARS" tamano="sm" codigo={false} /> al dólar
                  de <span className="tabular-nums">{formatearCotizacion(cotizacion)}</span>.
                </>
              ) : undefined
            }
          />

          <div className="space-y-2">
            <TituloSeccion nivel="etiqueta">¿Con qué paga?</TituloSeccion>
            {/* Sin `onMonedaChange` la moneda de cada fila queda fija: la impone
                la caja elegida y no se puede contradecir. */}
            <EditorPagos
              pagos={pagos}
              onChange={setPagos}
              cuentas={cuentas}
              objetivo={saldo}
              monedaObjetivo={anclaje}
              cotizacion={cotizacion}
            />
          </div>

          {/* Cuando la caja elegida no es la del anclaje, se muestra la
              equivalencia: la mayoria paga en pesos una deuda en dolares y hay
              que ver cuanto de la deuda cubre eso. */}
          {otraMoneda && (
            <Aviso tono="neutral" compacto>
              Ese pago cubre <Importe monto={resultado.cobrado} moneda={anclaje} tamano="sm" /> de
              la deuda, al dólar de{" "}
              <span className="tabular-nums">{formatearCotizacion(cotizacion)}</span>.
            </Aviso>
          )}
          {quedaElGrano && (
            <Aviso tono="neutral" compacto>
              No se llega exacto: {anclaje === "ARS" ? "US$ 0,01" : "$ 0,01"} son{" "}
              <Importe monto={grano} moneda={anclaje} tamano="sm" codigo={false} />. Esa diferencia
              se puede cobrar en {anclaje === "ARS" ? "pesos" : "dólares"}; si no, queda{" "}
              {resultado.quedaAFavor ? "de vuelto" : "en su cuenta"}.
            </Aviso>
          )}
        </DialogBody>

        {/* ── Que queda despues ── fuera del scroll, siempre a la vista. El
            estado lo dice el badge; el numero lleva el color de su moneda. */}
        <Card
          variant="muted"
          padding="compacto"
          className="flex items-center justify-between gap-4"
        >
          {resultado.cancelaTodo ? (
            <Badge variant="success">
              <Check aria-hidden="true" />
              Queda saldado
            </Badge>
          ) : hayVuelto ? (
            <Badge variant="success">
              <Check aria-hidden="true" />
              {propina ? "Saldado, deja de propina" : "Saldado, vuelto"}
            </Badge>
          ) : (
            <Badge variant="secondary">
              <ArrowRight aria-hidden="true" />
              Le sigue quedando
            </Badge>
          )}
          <span className="flex flex-col items-end gap-0.5">
            <Importe
              monto={hayVuelto ? vueltoArs : restaArs}
              moneda="ARS"
              tamano="lg"
              className="font-semibold"
            />
            {enDolares && !resultado.cancelaTodo && !hayVuelto && (
              <Importe monto={Math.abs(resultado.resta)} moneda="USD" tamano="sm" />
            )}
          </span>
        </Card>

        {hayVuelto && (
          <Aviso tono={vueltoSinCajon ? "warning" : "neutral"} compacto>
            <Checkbox
              checked={propina}
              onChange={(e) => setPropina(e.target.checked)}
              label="Lo deja de propina"
              ayuda="El vuelto no sale: queda en la caja."
            />
            {!propina && cajonVuelto ? (
              <p className="mt-1 text-xs">
                Paga más de lo que debe: el vuelto sale en pesos de {cajonVuelto.nombre}. No queda
                saldo a favor.
              </p>
            ) : null}
            {vueltoSinCajon ? (
              <p className="mt-1 text-xs">
                No hay una caja de Efectivo en pesos activa para dar el vuelto: el vuelto siempre
                sale en pesos. Activala en Caja o cobrá justo.
              </p>
            ) : null}
          </Aviso>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            type="button"
            cargando={guardando}
            disabled={!puede}
            onClick={() =>
              onConfirmar(
                validos.map((p) => ({
                  cuenta_id: p.cuenta_id,
                  medio: p.medio,
                  importe: p.importe,
                  ...(esCheque(p.medio) && p.cheque
                    ? { cheque: { ...p.cheque, tipo: p.medio as "CHEQUE" | "ECHEQ" } }
                    : {}),
                })),
                clave.current,
                { vuelto_propina: hayVuelto && propina },
              )
            }
          >
            Registrar el cobro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
